import { observable } from "@legendapp/state";
import { toast } from "react-hot-toast";

import { getLanguageName } from "@/i18n";
import {
  analyzeImageWithPrompt,
  analyzeImageWithStructuredOutput,
  transformImages,
} from "@/services/geminiService";

import {
  GET_IMAGE_IMPROVEMENTS_PROMPT,
  IMAGE_DESCRIPTION_PROMPT,
  IMAGE_IMPROVEMENTS_PROMPT,
  IMAGE_SCORE_PROMPT,
  IMAGE_SCORE_PROMPT_WITH_DESCRIPTION,
} from "./prompts_v2.0";

import { getSessionApiKey } from "@/services/apiKeyManager";
import { trackEvent } from "@/utils/tracking";

export type Category = {
  name: string;
  score: number;
  explanation: string;
  subCategories: SubCategory[];
};

export type SubCategory = {
  name: string;
  score: number;
  explanation: string;
};

export type Results = {
  overallScore: number;
  categories: Category[];
};

export type Improvement = {
  explanation: string;
  command: string;
  rank: number;
};

export enum STEP {
  UPLOAD = "upload",
  DESCRIPTION = "description",
  RESULTS = "results",
  IMPROVEMENTS = "improvements",
}

export interface ThumbnailReviewerState {
  step: STEP;
  image: string | null;
  description: string;
  isEditing: boolean;
  results: null | Results;
  improvedImage: string | null;
  improvedImageResults: null | Results;
  isLoading: boolean;
  improvements: Improvement[] | null;
  selectedImprovements: boolean[];
  usageLimitModal: {
    isOpen: boolean;
    type: "exceeded" | "remaining" | "last" | null;
    remainingCount?: number;
  };
  videoDescription: string;
  customCommands: string | null;
}

export const MAX_USAGE_DAILY = 50;

const getInitialState = (): ThumbnailReviewerState => ({
  step: STEP.UPLOAD,
  image: null,
  description: "",
  isEditing: false,
  results: null,
  improvedImage: null,
  improvedImageResults: null,
  isLoading: false,
  improvements: null,
  selectedImprovements: [],
  usageLimitModal: {
    isOpen: false,
    type: null,
    remainingCount: 0,
  },
  videoDescription: "",
  customCommands: null,
});

export const thumbnailReviewerState = observable<ThumbnailReviewerState>(getInitialState());

// Getter methods
export const getThumbnailReviewerState = {
  step: () => thumbnailReviewerState.step.get(),
  image: () => thumbnailReviewerState.image.get(),
  isLoading: () => thumbnailReviewerState.isLoading.get(),
  description: () => thumbnailReviewerState.description.get(),
  isEditing: () => thumbnailReviewerState.isEditing.get(),
  results: () => thumbnailReviewerState.results.get(),
  improvedImage: () => thumbnailReviewerState.improvedImage.get(),
  improvedImageResults: () => thumbnailReviewerState.improvedImageResults.get(),
  improvements: () => thumbnailReviewerState.improvements.get(),
  selectedImprovements: () => thumbnailReviewerState.selectedImprovements.get(),
  usageLimitModal: () => thumbnailReviewerState.usageLimitModal.get(),
  remainingUsageCount: () => thumbnailReviewerState.usageLimitModal.remainingCount.get(),
  isUsageLimitModalOpen: () => thumbnailReviewerState.usageLimitModal.isOpen.get(),
  usageLimitModalType: () => thumbnailReviewerState.usageLimitModal.type.get(),
  videoDescription: () => thumbnailReviewerState.videoDescription.get(),
  customCommands: () => thumbnailReviewerState.customCommands.get(),
};

// Setter methods
export const setThumbnailReviewerState = {
  step: (value: ThumbnailReviewerState["step"]) => thumbnailReviewerState.step.set(value),
  image: (value: ThumbnailReviewerState["image"]) => thumbnailReviewerState.image.set(value),
  isLoading: (value: boolean) => thumbnailReviewerState.isLoading.set(value),
  description: (value: string) => thumbnailReviewerState.description.set(value),
  isEditing: (value: boolean) => thumbnailReviewerState.isEditing.set(value),
  results: (value: ThumbnailReviewerState["results"]) => thumbnailReviewerState.results.set(value),
  improvedImage: (value: ThumbnailReviewerState["improvedImage"]) =>
    thumbnailReviewerState.improvedImage.set(value),
  improvedImageResults: (value: ThumbnailReviewerState["improvedImageResults"]) =>
    thumbnailReviewerState.improvedImageResults.set(value),
  improvements: (value: ThumbnailReviewerState["improvements"]) =>
    thumbnailReviewerState.improvements.set(value),
  selectedImprovements: (value: boolean[]) =>
    thumbnailReviewerState.selectedImprovements.set(value),
  usageLimitModal: (value: ThumbnailReviewerState["usageLimitModal"]) =>
    thumbnailReviewerState.usageLimitModal.set(value),
  closeUsageLimitModal: () => thumbnailReviewerState.usageLimitModal.isOpen.set(false),
  videoDescription: (value: string) => thumbnailReviewerState.videoDescription.set(value),
  customCommands: (value: ThumbnailReviewerState["customCommands"]) =>
    thumbnailReviewerState.customCommands.set(value),

  // Utility method to reset the state
  reset: () => thumbnailReviewerState.set(getInitialState()),
};

export const udpateUsageCount = (shouldOpenModal: boolean) => {
  const today = new Date().toISOString().split("T")[0]; // Get current date in YYYY-MM-DD format
  const usageKey = `thumbnail-reviewer-usage-${today}`;
  const currentUsage = parseInt(localStorage.getItem(usageKey) || "0", 10);
  console.log("Current usage:", currentUsage);

  if (currentUsage >= MAX_USAGE_DAILY) {
    setThumbnailReviewerState.usageLimitModal({
      isOpen: shouldOpenModal,
      type: "exceeded",
      remainingCount: 0,
    });
  } else if (currentUsage >= MAX_USAGE_DAILY - 1) {
    setThumbnailReviewerState.usageLimitModal({
      isOpen: false,
      type: "last",
      remainingCount: MAX_USAGE_DAILY - currentUsage,
    });
  } else {
    setThumbnailReviewerState.usageLimitModal({
      isOpen: false,
      type: "remaining",
      remainingCount: MAX_USAGE_DAILY - currentUsage,
    });
  }
};

/**
 * Checks if the user has exceeded their daily usage limit
 * @param t Translation function
 * @returns Object containing usage information and whether the limit is exceeded
 */
export const checkUsageLimit = () => {
  const today = new Date().toISOString().split("T")[0]; // Get current date in YYYY-MM-DD format
  const usageKey = `thumbnail-reviewer-usage-${today}`;
  const currentUsage = parseInt(localStorage.getItem(usageKey) || "0", 10);
  console.log("Current usage:", currentUsage);

  const isLimitExceeded = currentUsage >= MAX_USAGE_DAILY;

  if (isLimitExceeded) {
    // Show exceeded limit modal
    setThumbnailReviewerState.usageLimitModal({
      isOpen: true,
      type: "exceeded",
      remainingCount: 0,
    });
  }

  return {
    currentUsage,
    isLimitExceeded,
    usageKey,
    incrementUsage: () => {
      const newUsage = currentUsage + 1;
      localStorage.setItem(usageKey, newUsage.toString());

      // Only show exceeded limit modal if limit is reached
      if (newUsage >= MAX_USAGE_DAILY) {
        setThumbnailReviewerState.usageLimitModal({
          isOpen: true,
          type: "exceeded",
        });
      }
      if (newUsage >= MAX_USAGE_DAILY - 1) {
        setThumbnailReviewerState.usageLimitModal({
          isOpen: false,
          type: "last",
          remainingCount: MAX_USAGE_DAILY - newUsage,
        });
      } else {
        setThumbnailReviewerState.usageLimitModal({
          isOpen: false,
          type: "remaining",
          remainingCount: MAX_USAGE_DAILY - newUsage,
        });
      }
    },
  };
};

export const getImageDescription = async () => {
  if (!getThumbnailReviewerState.isLoading()) {
    return;
  }

  try {
    // Get the session API key
    const apiKey = getSessionApiKey();

    // Add debug logging
    console.log("API Key exists:", !!apiKey);
    setThumbnailReviewerState.isLoading(true);
    // Call Gemini API to analyze image
    const imageData = await getImageData();

    const prompt = IMAGE_DESCRIPTION_PROMPT.replace("{{language}}", getLanguageName());
    console.log("Calling Gemini API...");

    const description = await analyzeImageWithPrompt(prompt, imageData, apiKey);
    setThumbnailReviewerState.description(description);
    trackEvent("thumbnail_reviewer_got_image_description");
  } catch (error) {
    handleError(error);
  } finally {
    setThumbnailReviewerState.isLoading(false);
  }
};

// Refactor getImageData to accept an optional URL parameter
const getImageData = async (imageUrl?: string | null) => {
  // If no URL is provided, use the one from state
  const urlToUse = imageUrl || getThumbnailReviewerState.image();

  if (!urlToUse) {
    throw new Error("No image URL available");
  }

  // Convert URL blob to base64
  const imgResponse = await fetch(urlToUse);
  const blob = await imgResponse.blob();
  const base64 = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      // Remove the data URL prefix
      resolve(base64.split(",")[1]);
    };
    reader.readAsDataURL(blob);
  });

  const imageData = {
    base64,
    mimeType: blob.type,
  };
  return imageData;
};

export const getImageScores = async (imageToEvaluate?: string | null) => {
  const { isLimitExceeded, incrementUsage } = checkUsageLimit();

  if (isLimitExceeded) {
    return;
  }
  try {
    setThumbnailReviewerState.isLoading(true);
    if (
      !getThumbnailReviewerState.description() ||
      getThumbnailReviewerState.description().length === 0
    ) {
      trackEvent("thumbnail_reviewer_start_evaluation");
      await getImageDescription();
      setThumbnailReviewerState.step(STEP.DESCRIPTION);
      // Increment usage count after successful API call
      incrementUsage();
    }

    // Use the refactored getImageData with optional URL parameter
    const imageData = await getImageData(imageToEvaluate);

    const language = getLanguageName();
    const apiKey = getSessionApiKey();

    const mainPrompt =
      getThumbnailReviewerState.description().length > 0
        ? IMAGE_SCORE_PROMPT_WITH_DESCRIPTION.replace(
            "{{videoDescription}}",
            getThumbnailReviewerState.videoDescription()
          )
        : IMAGE_SCORE_PROMPT;

    const prompt = mainPrompt
      .replace("{{language}}", language)
      .replace("{{description}}", getThumbnailReviewerState.description());

    const schema = {
      type: "OBJECT",
      properties: {
        overallScore: { type: "INTEGER" },
        categories: {
          type: "ARRAY",
          items: {
            type: "OBJECT",
            properties: {
              name: { type: "STRING" },
              explanation: { type: "STRING" },
              score: { type: "INTEGER", format: "int32" },
              subCategories: {
                type: "ARRAY",
                items: {
                  type: "OBJECT",
                  properties: {
                    name: { type: "STRING" },
                    explanation: { type: "STRING" },
                    score: { type: "INTEGER", format: "int32" },
                  },
                  required: ["name", "explanation", "score"],
                },
              },
            },
            propertyOrdering: ["name", "explanation", "score", "subCategories"],
            required: ["name", "explanation", "score", "subCategories"],
          },
        },
      },
      required: ["overallScore", "categories"],
    };
    console.log("Calling Gemini API...");

    const response = await analyzeImageWithStructuredOutput(prompt, imageData, apiKey, schema);
    console.log("Response:", response);

    // If evaluating the improved image, save to improvedImageResults
    // Otherwise save to regular results
    if (imageToEvaluate) {
      setThumbnailReviewerState.improvedImageResults(response as Results);
    } else {
      // Save the response to state
      setThumbnailReviewerState.results(response as Results);
      setThumbnailReviewerState.step(STEP.RESULTS);
      trackEvent("thumbnail_reviewer_got_image_scores");
      await getImageImprovements();
    }
  } catch (error) {
    handleError(error);
  } finally {
    setThumbnailReviewerState.isLoading(false);
  }
};

export const getImageImprovements = async () => {
  try {
    setThumbnailReviewerState.isLoading(true);
    const language = getLanguageName();
    const apiKey = getSessionApiKey();
    const prompt = GET_IMAGE_IMPROVEMENTS_PROMPT.replace("{{language}}", language)
      .replace("{{results}}", JSON.stringify(getThumbnailReviewerState.results()))
      .replace("{{description}}", getThumbnailReviewerState.description());

    const schema = {
      type: "OBJECT",
      properties: {
        improvements: {
          type: "ARRAY",
          items: {
            type: "OBJECT",
            properties: {
              explanation: { type: "STRING" },
              command: { type: "STRING" },
              rank: { type: "INTEGER" },
            },
            required: ["explanation", "command", "rank"],
          },
        },
      },
    };

    const response = await analyzeImageWithStructuredOutput(prompt, undefined, apiKey, schema);
    console.log("Response:", response);
    if (typeof response === "object" && response !== null && "improvements" in response) {
      const improvements = response.improvements as Improvement[];
      setThumbnailReviewerState.improvements(improvements);

      // Initialize selected improvements (default first 3 are selected)
      setThumbnailReviewerState.selectedImprovements(improvements.map((_, index) => index < 3));

      // Initialize custom commands with the top 3 improvement commands
      const initialCommands = improvements
        .filter((_, index) => index < 3)
        .map((improvement) => improvement?.command || "")
        .join("\n");
      setThumbnailReviewerState.customCommands(initialCommands);
      trackEvent("thumbnail_reviewer_got_image_improvements");
    } else {
      throw new Error("Invalid response format from API");
    }
  } catch (error) {
    handleError(error);
  }
};

export const evaluateImprovedImage = async () => {
  const improvedImageUrl = getThumbnailReviewerState.improvedImage();
  if (!improvedImageUrl) {
    console.error("No improved image available to evaluate");
    return;
  }

  await getImageScores(improvedImageUrl);
};

export const applyImprovements = async (customCommands?: string | null) => {
  if (getThumbnailReviewerState.isLoading()) {
    return;
  }
  try {
    setThumbnailReviewerState.isLoading(true);
    const imageData = await getImageData();
    const language = getLanguageName();
    const apiKey = getSessionApiKey();

    // Get aspect ratio by loading the image
    const aspectRatio = await new Promise<string>((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(img.width > img.height ? "16:9" : "9:16");
      };
      img.src = getThumbnailReviewerState.image() || "";
    });

    // Use custom commands if provided, otherwise use selected improvements
    let commandsToApply;
    if (customCommands) {
      // Split custom commands by newline and filter out empty lines
      commandsToApply = customCommands.split("\n").filter((cmd) => cmd.trim().length > 0);
    } else {
      // Use the original selected improvements logic
      const selectedImprovements = getThumbnailReviewerState.selectedImprovements();
      const improvements = getThumbnailReviewerState.improvements();
      commandsToApply = improvements
        .filter((_, index) => selectedImprovements[index])
        .map((improvement) => improvement.command);
    }

    const prompt = `
    ${IMAGE_IMPROVEMENTS_PROMPT}
    ${commandsToApply.join("\n")}
    `
      .replace("{{language}}", language)
      .replace("{{aspectRatio}}", aspectRatio);

    console.log("Calling Gemini API...", prompt);

    const result = await transformImages(apiKey, imageData, null, prompt, undefined);
    const image = `data:image/png;base64,${result}`;
    setThumbnailReviewerState.improvedImage(image);
    setThumbnailReviewerState.step(STEP.IMPROVEMENTS);
    trackEvent("thumbnail_reviewer_applied_improvements");
    // Evaluate the improved image after it's generated
    await evaluateImprovedImage();
  } catch (error) {
    console.error("Error applying improvements:", error);
  } finally {
    setThumbnailReviewerState.isLoading(false);
  }
};

const handleError = (error: any) => {
  console.error("Error analyzing image:", error);
  if (error instanceof Error) {
    toast.error(`Failed to analyze image: ${error.message}`);
  } else {
    toast.error("Failed to analyze image. Please try again.");
  }
};
